import { useCallback, useEffect, useRef, useState } from 'react';
import { data_from_server_get } from '@/shared/http/client';

export const REFRESH_INTERVAL_MS = 60_000;

/**
 * Hook that supplies Inference & Monitoring tab data from the backend.
 *
 * Fetches GET /inference-monitoring/{workspaceId}/{usecaseId}?range={timeRange}
 * which queries BigQuery (mvstats, stats, prompts) and returns the shaped payload,
 * then refetches every minute while the page is visible, matching the
 * materialized view's one-minute staleness.
 *
 * Falls back to null data (empty state) when the request fails or the ids are missing.
 */
export function useInferenceData({ workspaceId, usecaseId, timeRange = 'last-hour' }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Keep a ref to abort stale in-flight fetches when deps change
    const fetchIdRef = useRef(0);

    const fetch = useCallback(() => {
        if (!workspaceId || !usecaseId) return;

        const fetchId = ++fetchIdRef.current;
        setLoading(true);
        setError(null);

        data_from_server_get(
            `inference-monitoring/${workspaceId}/${usecaseId}?range=${timeRange}`,
            (responseData) => {
                if (fetchId !== fetchIdRef.current) return; // stale
                setData(responseData);
                setLastUpdated(new Date());
                setLoading(false);
            },
        ).catch((err) => {
            if (fetchId !== fetchIdRef.current) return; // stale
            setError(err?.message || 'Failed to load inference data');
            setLoading(false);
        });
    }, [workspaceId, usecaseId, timeRange]);

    useEffect(() => {
        fetch();
        const intervalId = setInterval(() => {
            if (document.visibilityState === 'visible') fetch();
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [fetch]);

    return {
        data,
        loading,
        error,
        lastUpdated,
        refresh: fetch,
    };
}
